import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import Title from 'features/App/Title';
import { Card, CardSection } from 'components/Card';
import { Form, Input, PhoneNumberInput } from 'components/HookForm';
import Message from 'components/Message';

import { STAFF_REGEX, SMALL_TEXT_MAX_CHARACTERS } from 'constants/logic';
import { RecurringSchedule } from '../../Components/RecurringSchedule';
import { SingleDayExceptions } from '../../Components/SingleDayExceptions';
import ServicesPicker from '../../Components/ServicesPicker/ServicesPickerContainer';
import { messages } from '../Practitioners.messages';
import FooterForm from '../../FooterForm/FooterFormContainer';
import './PractitionerForm.scss';

const { block, element } = bem('practitioner-form');

function PractitionerForm(props) {
  const navigate = useNavigate();

  const {
    countryCodeShort,
    defaultValues,
    deletePractitioner,
    fullName,
    handleCancel,
    handleSubmit,
    id,
    isLoaded,
    isLoading,
    servicesList,
    showDeleteButton,
    title,
    venueId,
  } = props;

  const handleClickCancel = () => {
    navigate(-1);
  };

  if (!isLoaded) {
    return <Card loading />;
  }

  return (
    <Form
      key={id}
      id="practitionerForm"
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onReset={handleCancel}
      isLoading={!isLoaded}
      className={block()}>
      <Card flush className={element('card')}>
        <Message as={Title} message={title} values={{ fullName }} />

        <CardSection>
          <Message message={messages.form.practitionerInfo} as="div" className={element('section-label')} />

          <div className="grid">
            <div className="grid__item 1/1 md-1/2" data-cypress="practitioner-form-first-name">
              <Input
                name="firstName"
                label={messages.form.firstName}
                placeholder={messages.form.firstNamePlaceholder}
                maxLength={SMALL_TEXT_MAX_CHARACTERS}
                required
                pattern={{
                  value: STAFF_REGEX,
                  message: messages.validator.nameContainsStaff,
                }}
              />
            </div>

            <div className="grid__item 1/1 md-1/2" data-cypress="practitioner-form-last-name">
              <Input
                name="lastName"
                label={messages.form.lastName}
                placeholder={messages.form.lastNamePlaceholder}
                maxLength={SMALL_TEXT_MAX_CHARACTERS}
                optional
                pattern={{
                  value: STAFF_REGEX,
                  message: messages.validator.nameContainsStaff,
                }}
              />
            </div>

            <div className="grid__item 1/1 md-1/2" data-cypress="practitioner-form-email">
              <Input
                name="email"
                type="email"
                label={messages.form.emailAddress}
                placeholder={messages.form.emailAddressPlaceholder}
                optional
              />
            </div>

            <div className="grid__item 1/1 md-1/2" data-cypress="practitioner-form-phone">
              <PhoneNumberInput
                name="phone"
                label={messages.form.phoneNumber}
                optional
                countryCode={countryCodeShort}
              />
            </div>
          </div>

          <hr className="mv+" />
          <RecurringSchedule name="recurrence" />
          <hr className="mv+" />
          <SingleDayExceptions name="singleDayExceptions" venueId={venueId} />
          <hr className="mv+" />
          <ServicesPicker name="serviceIds" servicesList={servicesList} description={messages.form.serviceHint} />
        </CardSection>
        <FooterForm
          deleteMessage={messages.details.deletePractitioner}
          formName="practitionerForm"
          visible
          submitting={isLoading}
          showDelete={showDeleteButton}
          onClickCancel={handleClickCancel}
          onClickDelete={deletePractitioner}
        />
      </Card>
    </Form>
  );
}

PractitionerForm.propTypes = {
  countryCodeShort: PropTypes.string,
  defaultValues: PropTypes.shape({}),
  deletePractitioner: PropTypes.func,
  fullName: PropTypes.string,
  handleCancel: PropTypes.func,
  handleSubmit: PropTypes.func,
  id: PropTypes.number,
  isLoaded: PropTypes.bool,
  isLoading: PropTypes.bool,
  showDeleteButton: PropTypes.bool,
  servicesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.id,
      name: PropTypes.string,
      duration: PropTypes.string,
    })
  ),
  title: intlMessage,
  venueId: PropTypes.number,
};

export default PractitionerForm;
